import { WorkspacePostingOpportunitiesAPI } from './API'
import { collection } from '../../infra/collection'
import WorkspacePostingOpportunity from './WorkspacePostingOpportunity'
import { PrimeWorkspace } from '../../iam/WorkspaceStore'
import { makeObservable, computed } from 'mobx'
import { CursorPaged } from '../../types/definitions'
import { WorkspacePostingOpportunityDTO } from './dtos/workspace-posting-opportunity'
import { FetchUserData } from '../../iam/UserDataStore'
import once from 'lodash/once'
import { GetCurrentUser } from '../../iam/SessionStore'
import { UserDataType } from '../../iam/models/UserDataType'
import { ListWorkspacePostingOpportunitiesQuery } from './dtos/queries'
import { Observable } from 'rxjs'
import { User } from '@auth0/auth0-spa-js'
import { take, switchMap } from 'rxjs/operators'
import { shareLatest } from '../../utils/rx-util'

/**
 * Delegate for listing Workspace opportunities.
 */
export type ListWorkspacePostingOpportunitiesDelegate = (
  query: ListWorkspacePostingOpportunitiesQuery,
) => Promise<CursorPaged<WorkspacePostingOpportunity>>

export default class WorkspacePostingOpportunityStore {
  /**
   * Opportunities collection.
   */
  opportunities = collection<
    WorkspacePostingOpportunityDTO,
    WorkspacePostingOpportunity
  >({
    create: () => new WorkspacePostingOpportunity(),
  })

  /**
   * Observable that, when first subscribed to, probes whether we can view opportunities.
   */
  readonly canViewOpportunities$ = this.currentUser$
    .pipe(
      take(1),
      switchMap(() => this.canViewWorkspaceOpportunities()),
    )
    .pipe(shareLatest())

  constructor(
    private opsAPI: WorkspacePostingOpportunitiesAPI,
    private primeWorkspace: PrimeWorkspace,
    private getCurrentUser: GetCurrentUser,
    private fetchUserData: FetchUserData,
    private currentUser$: Observable<User>,
  ) {
    makeObservable(this, {
      opportunityCount: computed,
    })
    this.canViewOpportunities$.subscribe()
  }

  canViewWorkspaceOpportunities = once(async () => {
    try {
      const userData = await this.fetchUserData(this.getCurrentUser().publicId)
      return userData.userDataType === UserDataType.Pro
    } catch (e) {
      console.error(e)
      return false
    }
  })

  get opportunityCount() {
    return this.opportunities.length
  }

  async listOpportunities(
    query: ListWorkspacePostingOpportunitiesQuery,
  ): Promise<CursorPaged<WorkspacePostingOpportunity>> {
    return this.opsAPI.listOpportunities(query).then((r) => ({
      data: this.opportunities.set(r.data),
      cursor: r.cursor,
      total: r.total,
    }))
  }

  async applyToOpportunity(opportunityId: string) {
    const opportunity = this.opportunities.get(opportunityId)
    if (!opportunity) {
      return
    }
    await this.opsAPI.apply(opportunityId).then(() => {
      opportunity.set({
        isApplicant: true,
      })
    })
  }

  async withdrawFromOpportunity(opportunityId: string) {
    const opportunity = this.opportunities.get(opportunityId)
    if (!opportunity) {
      return
    }
    await this.opsAPI.withdraw(opportunityId).then(() => {
      opportunity.set({
        isApplicant: false,
      })
    })
  }
}
